/**
 * The left half: four fields in the order the backend forces, all of them on screen at once.
 *
 * ── No steps, and every field is drawn from the first frame ─────────────────────────────────
 * `model/assignForm.ts` decides which fields exist and which can be answered yet; this renders the
 * answer. A field that appeared as the one above it was answered would move everything below it,
 * and a trainer who cannot see the question cannot plan the four answers.
 *
 * ── The client picker's ROW is written here ──────────────────────────────────────────────────
 * `shared/ui/PickerList` asks "pick exactly one of N" — the search, the live count, the reserved box, the
 * three states and the radio group. The row is a render prop, written beside the field it belongs
 * to. The program picker that used to be its second caller moved to `features/create-plan` with the
 * client entry on 23 September 2026.
 *
 * ── The start field's help sentence is the mid-week rule, said before it bites ──────────────
 * Today a trainer discovers it from a schedule that is one session short. `preStartDrops` counts
 * the struck sessions from the SAME rule the generator applies and WORDS them — which of three
 * sentences applies and which declension table each weekday comes from are decisions, and they live
 * where a test reaches them. Nothing about that sentence is made here.
 *
 * ── The sentence is DESCRIBED and ANNOUNCED, not just printed ───────────────────────────────
 * It is a consequence of the very choice the grid under it is for, and it changes on every press
 * inside that grid. Printed alone it reaches a sighted reader and nobody else: the grid names it
 * through `aria-describedby`, and the live region below speaks it when it changes, because focus
 * stays in the grid and nothing else would say that a session just dropped out of the plan.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import choice from '~/shared/ui/choiceRow.module.css';
import { PickerList } from '~/shared/ui/PickerList';

import type { AssignFlow } from '../lib/useAssignFlow';
import { startHelpSentence } from '../model/preStartDrops';

import { AssignField } from './AssignField';
import { ModeRows } from './ModeRows';
import { StartMonthPicker } from './StartMonthPicker';
import { TimesList } from './TimesList';

interface AssignFormProps {
  readonly flow: AssignFlow;
  readonly locale: string;
}

export function AssignForm({ flow, locale }: AssignFormProps) {
  const { t } = useTranslation();

  const startHelp = startHelpSentence(
    {
      startDate: flow.startDate,
      drops: flow.drops,
      programmedWeekdays: flow.programmedWeekdays,
    },
    t,
  );
  // Derived during render rather than in an effect: the announcement is a function of the sentence
  // and one previous value, and an effect would speak it a frame after the grid has already moved
  // on. `announced` stays empty until the sentence FIRST changes, so opening the dialog does not
  // read the form's help out loud before the trainer has done anything.
  // The plan read landing also rewrites the sentence (from no weekdays to the programmed ones); that
  // is the dialog opening, not the trainer acting, so it is remembered but not spoken.
  const ready = flow.status === 'ready';
  const [seen, setSeen] = useState({ text: startHelp, ready });
  const [announced, setAnnounced] = useState('');
  if (seen.text !== startHelp) {
    if (seen.ready && ready) setAnnounced(startHelp);
    setSeen({ text: startHelp, ready });
  } else if (seen.ready !== ready) {
    setSeen({ text: startHelp, ready });
  }

  return (
    <>
      {/* Which rows exist, which are excluded and how a search matches all live in
        * `entities/client`'s `assignableClients` — an offer to somebody who is not yet a client
        * and a deactivated client are not offered; an invited roster entry is (R2). */}
      <AssignField name="client" gate={flow.gateOf('client')} label={t('web:assign.field.client')} required>
        {(lock) => (
          <PickerList
            {...lock}
            copy="web:assign.client"
            status={flow.clients.status}
            rows={flow.clients.rows}
            total={flow.clients.total}
            query={flow.clients.query}
            onQuery={flow.clients.setQuery}
            onRetry={flow.clients.retry}
            selectedId={flow.customerId}
            onSelect={flow.selectClient}
            idOf={(client) => client.customerId}
            renderRow={(client) => (
              <span className={choice.rowText}>
                <span className={choice.rowTitle}>{client.label}</span>
                {client.email !== null && <span className={choice.rowMeta}>{client.email}</span>}
              </span>
            )}
          />
        )}
      </AssignField>

      <AssignField name="mode" gate={flow.gateOf('mode')} label={t('web:assign.field.mode')}>
        {(lock) => (
          <ModeRows {...lock} modes={flow.modes} mode={flow.mode} onChange={flow.setMode} />
        )}
      </AssignField>

      <AssignField
        name="times"
        gate={flow.gateOf('times')}
        label={t('web:assign.field.times')}
        required
        help={t('web:assign.times.oneEachWeekday')}
      >
        {(lock) => <TimesList {...lock} slots={flow.timeSlots} onChange={flow.setSlotTime} />}
      </AssignField>

      <AssignField
        name="start"
        gate={flow.gateOf('start')}
        label={t('web:assign.field.start')}
        required
        help={startHelp}
      >
        {(lock) => (
          <StartMonthPicker
            {...lock}
            value={flow.startDate}
            window={flow.window}
            programmedWeekdays={flow.programmedWeekdays}
            onChange={flow.setStartDate}
            gridLabel={t('web:assign.start.gridLabel')}
            locale={locale}
          />
        )}
      </AssignField>

      <LiveRegionAnnouncer message={announced} />
    </>
  );
}
